import React, { useState, useEffect } from 'react';
import {
    Settings,
    Save,
    RotateCcw,
    Clock,
    Calendar,
    AlertCircle,
    CheckCircle
} from 'lucide-react';
import apiService from '../services/api';

const ConfigManagement = () => {
    const [configs, setConfigs] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedUsername, setSelectedUsername] = useState('');
    const [currentConfig, setCurrentConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const daysOfWeek = [
        'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
    ];

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedUsername && configs.length > 0) {
            const userConfig = configs.find(c => c.username === selectedUsername);
            setCurrentConfig(userConfig || null);
        }
    }, [selectedUsername, configs]);

    const fetchInitialData = async () => {
        try {
            setError('');

            // Fetch both configs and users in parallel
            const [configData, usersData] = await Promise.all([
                apiService.getConfig(),
                apiService.getUsers()
            ]);

            // Filter out default config (username === null), only show user configs
            const userConfigs = configData.filter(config => config.username !== null);
            setConfigs(configData); // Keep all configs for API calls
            setUsers(usersData);

            // Auto-select first user if available
            if (usersData.length > 0) {
                setSelectedUsername(usersData[0].username);
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to fetch configuration data');
        } finally {
            setLoading(false);
        }
    };

    const createUserConfigFromDefault = (username) => {
        const defaultConfig = configs.find(c => c.username === null);
        if (!defaultConfig) return null;

        return {
            ...defaultConfig,
            _id: undefined, // Remove _id so it creates a new document
            username: username
        };
    };

    const handleSave = async () => {
        if (!currentConfig || !selectedUsername) {
            setError('No configuration selected to save');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            // Update the specific user config in the configs array
            const updatedConfigs = [...configs];
            const configIndex = updatedConfigs.findIndex(c => c.username === selectedUsername);

            if (configIndex >= 0) {
                updatedConfigs[configIndex] = currentConfig;
            } else {
                // If user config doesn't exist, create it based on default
                const newUserConfig = createUserConfigFromDefault(selectedUsername);
                if (newUserConfig) {
                    // Apply current changes to the new config
                    Object.assign(newUserConfig, currentConfig);
                    newUserConfig.username = selectedUsername;
                    updatedConfigs.push(newUserConfig);
                }
            }

            // Send all configs to API (it expects all configs together)
            await apiService.updateConfig(updatedConfigs);

            // Update local state
            setConfigs(updatedConfigs);
            setSuccess(`Configuration updated successfully for ${selectedUsername}`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error saving config:', err);
            setError(err.response?.data?.error || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setCurrentConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleScheduleChange = (day, index, field, value) => {
        setCurrentConfig(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [day]: prev.schedule[day].map((slot, i) =>
                    i === index ? { ...slot, [field]: value } : slot
                )
            }
        }));
    };

    const addScheduleSlot = (day) => {
        setCurrentConfig(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [day]: [...(prev.schedule[day] || []), { start: '09:00', end: '17:00' }]
            }
        }));
    };

    const removeScheduleSlot = (day, index) => {
        setCurrentConfig(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [day]: prev.schedule[day].filter((_, i) => i !== index)
            }
        }));
    };

    const handleReset = async () => {
        if (!selectedUsername) return;

        try {
            setError('');
            await fetchInitialData();
            setSuccess(`Configuration reset for ${selectedUsername}`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to reset configuration');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (users.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-500">No users found. Please create users first.</p>
                </div>
            </div>
        );
    }

    if (!currentConfig && selectedUsername) {
        // If user doesn't have a config, create one from default
        const defaultConfig = configs.find(c => c.username === null);
        if (defaultConfig) {
            const newUserConfig = createUserConfigFromDefault(selectedUsername);
            if (newUserConfig) {
                setCurrentConfig(newUserConfig);
            }
        }
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">User Configuration</h1>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={handleReset}
                        disabled={saving || !selectedUsername}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                    >
                        <RotateCcw className="w-4 h-4" />
                        Reset
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || !selectedUsername || !currentConfig}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {/* User Selection */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Settings className="w-5 h-5 text-gray-500" />
                    <h2 className="text-xl font-semibold text-gray-900">Select User</h2>
                </div>

                <div className="max-w-md">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        User to Configure
                    </label>
                    <select
                        value={selectedUsername}
                        onChange={(e) => setSelectedUsername(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Select a user...</option>
                        {users.map((user) => (
                            <option key={user.username} value={user.username}>
                                {user.username} {user.status === 'active' ? '(Active)' : '(Paused)'}
                            </option>
                        ))}
                    </select>
                    {selectedUsername && (
                        <p className="mt-2 text-sm text-gray-600">
                            Configuring settings for: <span className="font-medium">{selectedUsername}</span>
                        </p>
                    )}
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {success}
                </div>
            )}

            {/* Configuration Form - Only show if user is selected and config exists */}
            {selectedUsername && currentConfig && (
                <>
                    {/* General Settings */}
                    {/* <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-2 mb-6">
              <Settings className="w-5 h-5 text-gray-500" />
              <h2 className="text-xl font-semibold text-gray-900">General Settings</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Zone
                </label>
                <input
                  type="text"
                  value={currentConfig.timeZone || ''}
                  onChange={(e) => handleInputChange('timeZone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., America/New_York"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Check Interval (seconds)
                </label>
                <input
                  type="number"
                  value={currentConfig.leadCheckInterval || 0}
                  onChange={(e) => handleInputChange('leadCheckInterval', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                />
              </div>
            </div>
          </div> */}

                    {/* Timing Settings */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center gap-2 mb-6">
                            <Clock className="w-5 h-5 text-gray-500" />
                            <h2 className="text-xl font-semibold text-gray-900">Timing Settings</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule Start Variation (minutes)
                </label>
                <input
                  type="number"
                  value={currentConfig.scheduleStartVariationTime || 0}
                  onChange={(e) => handleInputChange('scheduleStartVariationTime', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule End Variation (minutes)
                </label>
                <input
                  type="number"
                  value={currentConfig.scheduleEndVariationTime || 0}
                  onChange={(e) => handleInputChange('scheduleEndVariationTime', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority Wait Time (seconds)
                </label>
                <input
                  type="number"
                  value={currentConfig.priorityWaitTime || 0}
                  onChange={(e) => handleInputChange('priorityWaitTime', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div> */}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Claim Min Wait Time (seconds)
                                </label>
                                <input
                                    type="number"
                                    value={currentConfig.claimMinWaitTime || 0}
                                    onChange={(e) => handleInputChange('claimMinWaitTime', parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    min="0"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Claim Max Wait Time (seconds)
                                </label>
                                <input
                                    type="number"
                                    value={currentConfig.claimMaxWaitTime || 0}
                                    onChange={(e) => handleInputChange('claimMaxWaitTime', parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    min="0"
                                />
                            </div>

                            {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Browser Reload Interval (minutes)
                </label>
                <input
                  type="number"
                  value={currentConfig.browserReloadInterval || 0}
                  onChange={(e) => handleInputChange('browserReloadInterval', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Browser Restart Interval (minutes)
                </label>
                <input
                  type="number"
                  value={currentConfig.browserRestartInterval || 0}
                  onChange={(e) => handleInputChange('browserRestartInterval', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div> */}
                        </div>
                    </div>

                    {/* Schedule Settings */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center gap-2 mb-6">
                            <Calendar className="w-5 h-5 text-gray-500" />
                            <h2 className="text-xl font-semibold text-gray-900">Weekly Schedule</h2>
                        </div>

                        <div className="space-y-6">
                            {daysOfWeek.map((day) => (
                                <div key={day} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-medium text-gray-900 capitalize">{day}</h3>
                                        <button
                                            onClick={() => addScheduleSlot(day)}
                                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                                        >
                                            Add Time Slot
                                        </button>
                                    </div>

                                    <div className="space-y-3">
                                        {(currentConfig.schedule?.[day] || []).map((slot, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <div className="flex items-center gap-2">
                                                    <label className="text-sm text-gray-600">From:</label>
                                                    <input
                                                        type="time"
                                                        value={slot.start || ''}
                                                        onChange={(e) => handleScheduleChange(day, index, 'start', e.target.value)}
                                                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <label className="text-sm text-gray-600">To:</label>
                                                    <input
                                                        type="time"
                                                        value={slot.end || ''}
                                                        onChange={(e) => handleScheduleChange(day, index, 'end', e.target.value)}
                                                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => removeScheduleSlot(day, index)}
                                                    className="px-2 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}

                                        {(!currentConfig.schedule?.[day] || currentConfig.schedule[day].length === 0) && (
                                            <p className="text-sm text-gray-500 italic">No schedule set for this day</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Show message when no user is selected */}
            {!selectedUsername && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-6 py-4 rounded-lg text-center">
                    <p className="text-lg font-medium">Select a user to configure their settings</p>
                    <p className="text-sm mt-1">Choose a user from the dropdown above to view and edit their configuration.</p>
                </div>
            )}
        </div>
    );
};

export default ConfigManagement;
